import { useState, useEffect } from 'react';
import { useSubscription } from '@/hooks/use-subscription';
import { differenceInDays } from 'date-fns';
import { useSession } from '@/hooks/use-session';
import { Clock, X, Zap, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const DISMISS_KEY = 'strykr_trial_countdown_dismissed'; // LocalStorage key

interface TrialCountdownProps {
  className?: string;
}

export const TrialCountdown = ({ className }: TrialCountdownProps) => {
  const { session } = useSession();
  const { isTrialing, openPricingModal } = useSubscription();
  // Initialize state from localStorage
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === 'true';
    } catch (e) {
      return false;
    }
  });
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  
  // Calculate days left in trial
  useEffect(() => {
    const calculateDaysLeft = async () => {
      if (isTrialing) {
        try {
          // Get data from Supabase Edge Function response
          const storedData = localStorage.getItem('strykr_subscription_data');
          if (storedData) {
            const data = JSON.parse(storedData);
            
            // Get trial end date from localStorage data
            const trialEndDate = data?.trial_end_date ? new Date(data.trial_end_date) : null;
            
            if (trialEndDate) {
              const today = new Date();
              const daysRemaining = Math.max(0, differenceInDays(trialEndDate, today));
              setDaysLeft(daysRemaining);
              console.log('[TrialCountdown] Trial days remaining:', daysRemaining);
            } else {
              // Fallback: use 7 days (default trial period) if end date not found
              setDaysLeft(7);
              console.log('[TrialCountdown] No trial end date found, using default 7 days');
            }
          }
        } catch (error) {
          console.error('[TrialCountdown] Error calculating trial days left:', error);
          // Set a default even if calculation fails
          setDaysLeft(7);
        }
      }
    };
    
    calculateDaysLeft();
  }, [isTrialing]);
  
  // For dev mode: check localStorage directly too
  useEffect(() => {
    // Check if we need to force trial mode for dev testing
    try {
      const storedData = localStorage.getItem('strykr_subscription_data');
      if (storedData) {
        const data = JSON.parse(storedData);
        if (data.subscription_status === 'trialing' && !isTrialing) {
          console.log('[DevMode] Showing trial countdown from localStorage override');
        }
      }
    } catch (e) {
      console.log('[DevMode] Error checking trial status:', e);
    }
  }, [isTrialing]);
  
  // Don't show if not in trial or already dismissed
  const showTrialBanner = isTrialing || 
    (localStorage.getItem('strykr_subscription_data') && 
     JSON.parse(localStorage.getItem('strykr_subscription_data') || '{}').subscription_status === 'trialing');
  
  // Check if we should show the banner
  if (!showTrialBanner || dismissed) {
    return null;
  }
  
  // If days left hasn't been calculated yet, show a loading state rather than nothing
  if (daysLeft === null) {
    // Setting a default of 7 days if calculation hasn't completed
    setDaysLeft(7);
  }
  
  // Use urgency colors based on days left
  const getColor = () => {
    if (daysLeft <= 1) return 'bg-red-500/10 text-red-500 border-red-500/20';
    if (daysLeft <= 3) return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
    return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
  };
  
  // Get message based on days left
  const getMessage = () => {
    if (daysLeft === 0) return 'Trial ends today!';
    if (daysLeft === 1) return 'Trial ends tomorrow!';
    return `${daysLeft} days left in your trial`;
  };
  
  // Determine if this is a critical notice (0-1 days left)
  const isCritical = daysLeft <= 1;
  
  return (
    <div className={cn(
      "w-full py-2 px-3 flex items-center justify-between text-xs relative overflow-hidden transition-all duration-300",
      "backdrop-blur-sm animate-in fade-in duration-300",
      isCritical ? 
        "bg-gradient-to-r from-red-500/10 via-red-500/5 to-red-500/10 text-red-500 border-b border-red-500/20" : 
        "bg-gradient-to-r from-blue-500/10 via-indigo-500/5 to-blue-500/10 text-blue-500 border-b border-blue-500/20"
    )}>
      {/* Animated pulse effect for critical notices */}
      {isCritical && (
        <div className="absolute inset-0 bg-red-500/5 animate-pulse opacity-50"></div>
      )}
      
      <div className="flex items-center space-x-2 z-10">
        {isCritical ? (
          <Zap className="h-4 w-4 animate-pulse text-red-500" />
        ) : (
          <Clock className="h-4 w-4" />
        )}
        <span className="font-medium">{getMessage()}</span>
        
        {/* Special offer tag on critical notices */}
        {isCritical && (
          <span className="bg-red-500/10 text-red-500 px-1.5 py-0.5 rounded-sm text-[10px] font-medium border border-red-500/20">
            Special offer
          </span>
        )}
      </div>
      
      <div className="flex items-center space-x-2 z-10">
        <Button
          size="sm"
          onClick={() => openPricingModal('trial_countdown')}
          className={cn(
            "h-7 gap-1 group transition-all duration-300 px-2 py-0",
            isCritical ? 
              "bg-red-500 hover:bg-red-600 text-white" : 
              "bg-blue-500 hover:bg-blue-600 text-white"
          )}
        >
          Upgrade now
          <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
        </Button>
        
        <button 
          onClick={() => {
            try {
              localStorage.setItem(DISMISS_KEY, 'true'); // Set in localStorage
            } catch (e) {
              console.error('Failed to save dismiss state:', e);
            }
            setDismissed(true); // Update local state
          }}
          className="hover:opacity-70 rounded-full h-5 w-5 flex items-center justify-center"
          aria-label="Dismiss notice"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
};
